// Copyright ©️ 2026 eVolve MEP, LLC

import React, { type ReactNode, useCallback } from 'react';

import { PART_TYPE_ASSEMBLY_ID, type PartListPart } from '@evolvemep/api-ts';

import { WrappedModal, type WrappedModalProps } from 'components/Mantine/Modals/WrappedModal';
import { useIsMobile } from 'hooks/useIsMobile';
import { useLocalStorage } from 'hooks/useLocalStorage';
import { CatalogSelect } from 'modules/Shop/CatalogSetup/CatalogSelect';
import {
  CatalogPartAdder,
  type PartToAddWithDecisions,
} from 'modules/Shop/WorkOrders/WorkOrderDetail/Items/FromCatalog/CatalogPartAdder';

export type AddItemsFromCatalogModalProps = {
  opened: boolean;
  onClose: () => void;
  onAddParts: (parts: PartToAddWithDecisions[]) => Promise<void>;
  /** Render the existing item list */
  children: ReactNode;
  disableAssemblies?: boolean;
} & Pick<WrappedModalProps, 'title' | 'subtitle' | 'miniHeader'>;

export const AddItemsFromCatalogModal = ({
  opened,
  onClose,
  onAddParts,
  children,
  disableAssemblies,
  ...props
}: AddItemsFromCatalogModalProps) => {
  const isMobile = useIsMobile();
  const [selectedCatalog, setSelectedCatalog] = useLocalStorage('SELECTED_CATALOG', null);

  const disablePart = useCallback(
    (p: PartListPart) => {
      if (!disableAssemblies) return false;
      return p.partTypeId === PART_TYPE_ASSEMBLY_ID;
    },
    [disableAssemblies],
  );

  return (
    <WrappedModal
      opened={opened}
      onClose={onClose}
      {...props}
      hideHeader={isMobile}
      headerRightComponent={
        <CatalogSelect onCatalogSelected={setSelectedCatalog} selectedCatalog={selectedCatalog} w={250} />
      }
      hideModalButtons
      fullScreen
      bodyProps={{
        flex: 1,
        p: 0,
      }}
    >
      <CatalogPartAdder
        partCatalog={selectedCatalog}
        onCatalogSelected={setSelectedCatalog}
        withDecisions
        onAddParts={onAddParts}
        existingPartList={children}
        disablePart={disablePart}
      />
    </WrappedModal>
  );
};
